import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreateStandardAccountRequest, StandardAccount, UpdateStandardAccountRequest } from '../../../api/types';
import vendorApi from '../../../api/vendorApi';

interface StandardAccountSelectorProps {
    currentAccountId?: string | null;
    onSelect: (account: StandardAccount) => void;
    businessId?: string;
    includeInternalTransfer?: boolean;
}

const getDisplayAccountNumber = (account: StandardAccount): string => {
    const number = (account.account_number || '').trim();
    if (number) return number;
    return '';
};

const getDisplayAccountName = (account: StandardAccount): string => {
    return account.display_name || account.account_name || 'Untitled Account';
};

const getDisplayBucket = (bucket?: string | null): string => ((bucket || '').trim().toUpperCase() === 'INTERNAL TRANSFER' ? 'Non-Operating Items' : (bucket || ''));

const compareAccountsByNumber = (left: StandardAccount, right: StandardAccount): number => {
    const leftNumber = (left.account_number || '').trim();
    const rightNumber = (right.account_number || '').trim();

    if (leftNumber && rightNumber) {
        const leftIsNumeric = /^\d+$/.test(leftNumber);
        const rightIsNumeric = /^\d+$/.test(rightNumber);
        if (leftIsNumeric && rightIsNumeric) {
            const delta = Number(leftNumber) - Number(rightNumber);
            if (delta !== 0) return delta;
        } else {
            const byString = leftNumber.localeCompare(rightNumber, undefined, { numeric: true });
            if (byString !== 0) return byString;
        }
    } else if (leftNumber || rightNumber) {
        return leftNumber ? -1 : 1;
    }

    return getDisplayAccountName(left).localeCompare(getDisplayAccountName(right));
};

const EMPTY_FORM: CreateStandardAccountRequest = {
    fiscal_bucket: '',
    account_name: '',
    account_number: '',
    notes: '',
    fs_category: 'Income Statement',
    non_operating: false,
};

const StandardAccountSelector: React.FC<StandardAccountSelectorProps & { isOpen: boolean; onClose: () => void }> = ({
    currentAccountId,
    onSelect,
    isOpen,
    onClose,
    businessId,
    includeInternalTransfer = true,
}) => {
    const queryClient = useQueryClient();
    const [activeBucket, setActiveBucket] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [editingAccount, setEditingAccount] = useState<StandardAccount | null>(null);
    const [isBucketMenuOpen, setIsBucketMenuOpen] = useState(false);
    const [formData, setFormData] = useState<CreateStandardAccountRequest>(EMPTY_FORM);

    const { data: accountList, isLoading } = useQuery({
        queryKey: ['standardAccounts', businessId],
        queryFn: () => vendorApi.getStandardAccounts(businessId),
        enabled: isOpen,
    });

    const internalTransferOption = useMemo(
        () =>
        ({
            _id: 'internal-transfer',
            account_name: 'Internal Transfer',
            display_name: 'Internal Transfer',
            account_number: '',
            fiscal_bucket: 'Non-Operating Items',
            non_operating: true,
            notes: 'Use for internal transfers',
            industry: accountList?.industry || '',
            business_type: accountList?.business_type,
            naics_code: accountList?.naics_code,
            source: 'template',
        } as StandardAccount),
        [accountList?.business_type, accountList?.industry, accountList?.naics_code],
    );

    const accounts = useMemo(() => {
        return includeInternalTransfer ? [...(accountList?.items || []), internalTransferOption] : [...(accountList?.items || [])];
    }, [accountList?.items, includeInternalTransfer, internalTransferOption]);

    const uniqueBuckets = useMemo(() => {
        const buckets = Array.from(new Set(accounts.map((account) => getDisplayBucket(account.fiscal_bucket) || 'Uncategorized')));
        const priority = ['Revenue', 'COGS', 'Labor', 'Fixed Expense', 'Other Expense', 'Non-Operating Items', 'Balance Sheet'];
        return buckets.sort((a, b) => {
            const idxA = priority.indexOf(a);
            const idxB = priority.indexOf(b);
            if (idxA !== -1 && idxB !== -1) return idxA - idxB;
            if (idxA !== -1) return -1;
            if (idxB !== -1) return 1;
            return a.localeCompare(b);
        });
    }, [accounts]);

    useEffect(() => {
        if (!activeBucket && uniqueBuckets.length > 0) {
            const current = currentAccountId ? accounts.find((account) => account._id === currentAccountId) : null;
            setActiveBucket(getDisplayBucket(current?.fiscal_bucket) || uniqueBuckets[0]);
        }
    }, [accounts, activeBucket, currentAccountId, uniqueBuckets]);

    useEffect(() => {
        if (isCreateOpen) {
            setFormData((prev) => ({
                ...prev,
                fiscal_bucket: prev.fiscal_bucket || activeBucket || '',
            }));
        }
    }, [activeBucket, isCreateOpen]);

    const filteredAccounts = useMemo(() => {
        let filtered = accounts;
        if (searchTerm.trim()) {
            const lowerTerm = searchTerm.toLowerCase();
            filtered = filtered.filter((account) => {
                const displayName = getDisplayAccountName(account).toLowerCase();
                const parentName = (account.parent_account_name || '').toLowerCase();
                const notes = (account.notes || '').toLowerCase();
                return (
                    displayName.includes(lowerTerm) ||
                    parentName.includes(lowerTerm) ||
                    getDisplayAccountNumber(account).toLowerCase().includes(lowerTerm) ||
                    notes.includes(lowerTerm)
                );
            });
        }

        const scoped = !searchTerm.trim()
            ? filtered.filter((account) => (getDisplayBucket(account.fiscal_bucket) || 'Uncategorized') === activeBucket)
            : filtered;

        return [...scoped].sort(compareAccountsByNumber);
    }, [accounts, activeBucket, searchTerm]);

    const selectableBuckets = useMemo(
        () => uniqueBuckets.filter((bucket) => bucket !== 'INTERNAL TRANSFER'),
        [uniqueBuckets],
    );

    const filteredCreateBuckets = useMemo(() => {
        const query = (formData.fiscal_bucket || '').trim().toLowerCase();
        if (!query) return selectableBuckets;
        return selectableBuckets.filter((bucket) => bucket.toLowerCase().includes(query));
    }, [formData.fiscal_bucket, selectableBuckets]);

    const createAccountMutation = useMutation({
        mutationFn: (payload: CreateStandardAccountRequest) => vendorApi.createStandardAccount(payload, businessId),
        onSuccess: (createdAccount) => {
            queryClient.invalidateQueries({ queryKey: ['standardAccounts', businessId] });
            queryClient.invalidateQueries({ queryKey: ['qboAccounts', businessId] });
            setActiveBucket(createdAccount.fiscal_bucket || activeBucket);
            setIsCreateOpen(false);
            setFormData(EMPTY_FORM);
            onSelect({
                ...createdAccount,
                account_name: getDisplayAccountName(createdAccount),
                display_name: getDisplayAccountName(createdAccount),
            });
            onClose();
        },
    });

    const updateAccountMutation = useMutation({
        mutationFn: ({ accountId, payload }: { accountId: string; payload: UpdateStandardAccountRequest }) =>
            vendorApi.updateStandardAccount(accountId, payload, businessId),
        onSuccess: (updatedAccount) => {
            queryClient.invalidateQueries({ queryKey: ['standardAccounts', businessId] });
            queryClient.invalidateQueries({ queryKey: ['qboAccounts', businessId] });
            setActiveBucket(updatedAccount.fiscal_bucket || activeBucket);
            setIsCreateOpen(false);
            setEditingAccount(null);
            setFormData(EMPTY_FORM);
        },
    });

    const deleteAccountMutation = useMutation({
        mutationFn: (accountId: string) => vendorApi.deleteStandardAccount(accountId, businessId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['standardAccounts', businessId] });
            queryClient.invalidateQueries({ queryKey: ['qboAccounts', businessId] });
        },
    });

    const handleCreateAccount = () => {
        if (!formData.fiscal_bucket?.trim() || !formData.account_name?.trim()) return;
        const payload = {
            fiscal_bucket: formData.fiscal_bucket.trim(),
            account_name: formData.account_name.trim(),
            parent_account_name: formData.parent_account_name?.trim() || undefined,
            account_number: formData.account_number?.trim() || undefined,
            notes: formData.notes?.trim() || undefined,
            fs_category: formData.fs_category?.trim() || undefined,
            non_operating: formData.non_operating,
        };
        if (editingAccount) {
            updateAccountMutation.mutate({ accountId: editingAccount._id, payload });
            return;
        }
        createAccountMutation.mutate(payload);
    };

    const openCreateModal = () => {
        setEditingAccount(null);
        setFormData({
            ...EMPTY_FORM,
            fiscal_bucket: activeBucket || '',
        });
        setIsCreateOpen(true);
    };

    const openEditModal = (account: StandardAccount) => {
        if (account._id === 'internal-transfer') return;
        setEditingAccount(account);
        setFormData({
            fiscal_bucket: getDisplayBucket(account.fiscal_bucket) || '',
            account_name: getDisplayAccountName(account),
            parent_account_name: account.parent_account_name || '',
            account_number: account.account_number || '',
            notes: account.notes || '',
            fs_category: account.fs_category || 'Income Statement',
            non_operating: !!account.non_operating || getDisplayBucket(account.fiscal_bucket) === 'Non-Operating Items',
        });
        setIsCreateOpen(true);
    };

    const closeEditor = () => {
        setIsCreateOpen(false);
        setEditingAccount(null);
        setFormData(EMPTY_FORM);
        setIsBucketMenuOpen(false);
    };

    const handleDeleteAccount = (account: StandardAccount) => {
        if (account._id === 'internal-transfer') return;
        if (!window.confirm(`Delete "${getDisplayAccountName(account)}"?`)) return;
        deleteAccountMutation.mutate(account._id);
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[120] overflow-y-auto" aria-labelledby="account-selector-title" role="dialog" aria-modal="true">
            <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>
                <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>

                <div className="inline-block max-h-[85vh] w-full max-w-2xl transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:align-middle">
                    <div className="border-b border-gray-100 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="mb-4 flex items-center justify-between">
                            <h3 className="text-lg font-medium leading-6 text-gray-900" id="account-selector-title">Select Finz Account</h3>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={openCreateModal}
                                    className="inline-flex items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
                                >
                                    <Plus className="h-3.5 w-3.5" />
                                    Add Finz Account
                                </button>
                                <button onClick={onClose} className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none">
                                    <span className="sr-only">Close</span>
                                    <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div className="relative mb-4 rounded-md shadow-sm">
                            <input
                                type="text"
                                className="block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                                placeholder="Search by name, parent, number, or notes..."
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                                autoFocus
                            />
                        </div>

                        {!searchTerm && (
                            <div className="flex items-center gap-2 overflow-x-auto pb-2 no-scrollbar">
                                {uniqueBuckets.map((bucket) => (
                                    <button
                                        key={bucket}
                                        onClick={() => setActiveBucket(bucket)}
                                        className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${activeBucket === bucket
                                            ? 'bg-indigo-100 text-indigo-700'
                                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                            }`}
                                    >
                                        {bucket}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="h-[420px] overflow-y-auto p-2">
                        {isLoading ? (
                            <div className="flex h-full flex-col items-center justify-center gap-2 text-gray-400">
                                <Loader2 className="h-5 w-5 animate-spin" />
                                <span className="text-sm">Loading accounts...</span>
                            </div>
                        ) : filteredAccounts.length === 0 ? (
                            <div className="flex h-full flex-col items-center justify-center text-sm text-gray-400">
                                <p>No accounts found.</p>
                            </div>
                        ) : (
                            <div className="space-y-1">
                                {filteredAccounts.map((account) => {
                                    const isSelected = account._id === currentAccountId;
                                    const displayNumber = getDisplayAccountNumber(account);
                                    const displayName = getDisplayAccountName(account);
                                    return (
                                        <div
                                            key={account._id}
                                            className={`flex items-start rounded-lg border p-3 text-left transition-all ${isSelected
                                                ? 'border-indigo-600 bg-indigo-50'
                                                : 'border-transparent hover:border-gray-200 hover:bg-gray-50'
                                                }`}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    onSelect({
                                                        ...account,
                                                        account_name: displayName,
                                                        display_name: displayName,
                                                        account_number: displayNumber,
                                                    });
                                                    onClose();
                                                }}
                                                className="min-w-0 flex-1 text-left"
                                            >
                                                <div className="mb-0.5 flex items-center justify-between gap-3">
                                                    <span className={`truncate text-sm font-medium ${isSelected ? 'text-indigo-900' : 'text-gray-900'}`}>
                                                        {displayName}
                                                    </span>
                                                    {isSelected && (
                                                        <span className="text-indigo-600">
                                                            <Check size={16} />
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2 text-[11px] text-gray-500">
                                                    {displayNumber && <span className="font-mono">{displayNumber}</span>}
                                                    <span>{getDisplayBucket(account.fiscal_bucket)}</span>
                                                </div>
                                                {account.notes && (
                                                    <p className="mt-1 line-clamp-1 text-xs text-gray-500">{account.notes}</p>
                                                )}
                                            </button>
                                            {account._id !== 'internal-transfer' && (
                                                <div className="ml-3 flex items-center gap-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => openEditModal(account)}
                                                        className="rounded-md p-1.5 text-gray-400 hover:bg-white hover:text-gray-600"
                                                        title="Edit Finz account"
                                                    >
                                                        <Pencil className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeleteAccount(account)}
                                                        className="rounded-md p-1.5 text-gray-400 hover:bg-white hover:text-red-600"
                                                        title="Delete Finz account"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {isCreateOpen && (
                <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/30 p-4">
                    <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
                        <div className="border-b border-gray-100 px-5 py-4">
                            <div className="flex items-center justify-between">
                                <h4 className="text-base font-semibold text-gray-900">{editingAccount ? 'Edit Finz Account' : 'Add Finz Account'}</h4>
                                <button
                                    type="button"
                                    onClick={closeEditor}
                                    className="text-gray-400 hover:text-gray-500"
                                >
                                    <span className="sr-only">Close</span>
                                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div className="space-y-4 px-5 py-4">
                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-700">Category</label>
                                <div className="relative">
                                    <input
                                        value={formData.fiscal_bucket || ''}
                                        onFocus={() => setIsBucketMenuOpen(true)}
                                            onChange={(event) => {
                                            const nextBucket = event.target.value;
                                            setFormData((prev) => ({
                                                ...prev,
                                                fiscal_bucket: nextBucket,
                                                non_operating: nextBucket.trim().toLowerCase() === 'non-operating items' ? true : prev.non_operating,
                                            }));
                                            setIsBucketMenuOpen(true);
                                        }}
                                        className="block w-full rounded-md border border-gray-300 px-3 py-2 pr-10 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                        placeholder="Select or type a category"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setIsBucketMenuOpen((prev) => !prev)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-500"
                                    >
                                        <ChevronDown className="h-4 w-4" />
                                    </button>
                                    {isBucketMenuOpen && filteredCreateBuckets.length > 0 && (
                                        <div className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg">
                                            {filteredCreateBuckets.map((bucket) => (
                                                <button
                                                    key={bucket}
                                                    type="button"
                                                    onMouseDown={(event) => event.preventDefault()}
                                                    onClick={() => {
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            fiscal_bucket: bucket,
                                                            non_operating: bucket.trim().toLowerCase() === 'non-operating items' ? true : prev.non_operating,
                                                        }));
                                                        setIsBucketMenuOpen(false);
                                                    }}
                                                    className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                                >
                                                    {bucket}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-700">Account Name</label>
                                <input
                                    value={formData.account_name || ''}
                                    onChange={(event) => setFormData((prev) => ({ ...prev, account_name: event.target.value }))}
                                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    placeholder="Property"
                                />
                            </div>

                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-700">Account Number <span className="font-normal text-gray-400">(Optional)</span></label>
                                <input
                                    value={formData.account_number || ''}
                                    onChange={(event) => setFormData((prev) => ({ ...prev, account_number: event.target.value }))}
                                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    placeholder="7210"
                                />
                            </div>

                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-700">Notes</label>
                                <textarea
                                    rows={3}
                                    value={formData.notes || ''}
                                    onChange={(event) => setFormData((prev) => ({ ...prev, notes: event.target.value }))}
                                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    placeholder="Optional description"
                                />
                            </div>

                            <label className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-3 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={!!formData.non_operating}
                                    onChange={(event) => setFormData((prev) => ({ ...prev, non_operating: event.target.checked }))}
                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                <div>
                                    <div className="font-medium text-gray-900">Non-operating</div>
                                    <div className="text-xs text-gray-500">Transactions mapped to this account default to `operating_flag = false`.</div>
                                </div>
                            </label>
                        </div>

                        <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-5 py-4">
                            <button
                                type="button"
                                onClick={closeEditor}
                                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleCreateAccount}
                                disabled={(createAccountMutation.isPending || updateAccountMutation.isPending) || !formData.fiscal_bucket?.trim() || !formData.account_name?.trim()}
                                className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
                            >
                                {(createAccountMutation.isPending || updateAccountMutation.isPending) && <Loader2 className="h-4 w-4 animate-spin" />}
                                {editingAccount ? 'Save Changes' : 'Save Account'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>,
        document.body,
    );
};

export default StandardAccountSelector;
